import { clearOfflineCache } from "core/cache/offlineCache";
import { buildRedirectUri } from "./redirectUri";

/**
 * Tear the session down and send the user to Keycloak to log in again.
 *
 * Everything the browser is holding goes first, in this order: the in-memory
 * query cache, its IndexedDB copy, then the OIDC tokens. Skipping any of them
 * leaves the app able to render the dead session — the persisted cache in
 * particular survives the reload and repaints the old user's data on the way
 * back in.
 *
 * The current URL is carried through `buildRedirectUri()`, so a user who was
 * deep in a workbook lands back on it. With the Keycloak SSO session still
 * alive the whole trip is invisible.
 *
 * @param {object} args
 * @param {object} args.auth - the `useAuth()` value.
 * @param {import("@tanstack/react-query").QueryClient} [args.queryClient]
 */
export const endSession = async ({ auth, queryClient }) => {
  try {
    queryClient?.clear();
    await clearOfflineCache();
    await auth.removeUser();
  } catch (error) {
    // A half-cleared session still has to reach the login page; stopping here
    // would strand the user on whatever error screen sent us.
    console.warn("Could not fully clear the session before logging in.", error);
  }

  await auth.signinRedirect({ redirect_uri: buildRedirectUri() });
};
